import React from 'react';

import ItemsProviderWithStore, {
	ItemsProviderWithStoreContextActions,
} from '@common/react/components/Core/ItemsProviderWithStore/ItemsProviderWithStore';
import { List } from '@common/typescript/objects/List';
import {
	BaseFilters,
	ItemsProvider,
	ItemsProviderContext,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';

import { Location } from '@app/objects/Location';
import { defaultOptions } from '@app/components/UI/Location/defaultOptions';

interface LocationsProviderFilter extends BaseFilters {
	companyId?: number;
	doctorId?: number;
	userIds?: Array<number>;
}

interface LocationsProviderActions extends ItemsProviderWithStoreContextActions<Location, LocationsProviderFilter, List<Location>> {
	getLocationByIdOrFirstUserLocation: (id: number, defaultLocation?: Location) => Location | undefined;
}

interface LocationsProviderContext extends ItemsProviderContext<Location,
	LocationsProviderFilter, List<Location>, LocationsProviderActions> {}

interface LocationsProviderProps {
	children?: React.ReactNode | ((context: LocationsProviderContext) => React.ReactNode);
	filters?: LocationsProviderFilter;
	withoutStore?: boolean;
	items?: Array<Location>;
}

export const LocationsProvider: React.FC<LocationsProviderProps> = (props) => {
	const {
		children, filters, withoutStore, items,
	} = props;
	if (withoutStore) {
		return <ItemsProvider<Location, LocationsProviderFilter>
			items={items}
			filters={filters}
			loadRequest={defaultOptions.loadRequest}
			pagination={defaultOptions.pagination}
			skipInitLoad={!!items}
		>
			{(context) =>
				(typeof children === 'function' ? children({
					...context,
					actions: {
						...context.actions,
						getByIdOrFirst: (id) => (id
							? context.state.items?.find((item) => item.id === id) ?? context.state.items[0]
							: context.state.items[0]),
						getLocationByIdOrFirstUserLocation: (id, defaultLocation) => (
							(!id || id < 0) && defaultLocation
								? context.state.items.some((item) => item.id === defaultLocation?.id)
									? defaultLocation
									: context.state.items[0]
								: id
									? context.state.items?.find((item) => item.id === id) ?? context.state.items[0]
									: context.state.items[0]
						),
					},
				} as LocationsProviderContext) : children)
			}
		</ItemsProvider>;
	}

	return <ItemsProviderWithStore<Location, LocationsProviderFilter>
		{...defaultOptions}
	>
		{(context) =>
			(typeof children === 'function' ? children({
				...context,
				actions: {
					...context.actions,
					getLocationByIdOrFirstUserLocation: (id, defaultLocation) => (
						(!id || id < 0) && defaultLocation
							? context.state.items.some((item) => item.id === defaultLocation?.id)
								? defaultLocation
								: context.state.items[0]
							: context.actions.getByIdOrFirst(id)
					),
				},
			} as LocationsProviderContext) : children)
		}
	</ItemsProviderWithStore>;
};
